import {Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {StepComponent} from "../../step/step.component";
import {Step, StepService} from "../../services/step.service";
import {ActivatedRoute, Router} from "@angular/router";
import {HeizcheckRequestService} from "../services/heizcheck-request.service";
import {ElementService} from "../../services/element.service";
import {PortalService} from "../../services/portal.service";
import {HeizcheckAnfrage} from "../anfrage/heizcheck.anfrage";
import {VerbrauchsanalyseHeizcheckForm} from "../verbrauchsanalyse/verbrauchsanalyse.heizcheck.form";
import {EnergyMapService} from "../../energy-map/services/energy.map.service";

/**
 * define global version to store if we should show the short version of the advisor and redirect to an external link on last page
 */
let globalStep: string = null;

/**
 * Overwrite the Generic step component for the first section fo the moderat.
 *
 * @inheritDoc
 */
@Component({
    selector: 'app-step-heizcheck-einstieg',
    templateUrl: './einstieg.component.html',
    standalone: false
})
export class EinstiegComponent extends StepComponent implements OnInit {

  /**
   * Parameter in the querystring for setting the step
   */
  static readonly PARAMETER_STEP = 'step';

  /**
   * Several versions/steps/shortcuts of the advisor
   */
  static readonly STEP_SHORT = 'short';
  static readonly STEP_START = 'start';

  /**
   * (input) data of the user
   */
  anfrage: HeizcheckAnfrage;

  /**
   * @inheritDoc
   */
  formConfig: VerbrauchsanalyseHeizcheckForm;

  /**
   * The stepform element
   */
  @ViewChild('formWrapper') formWrapper: ElementRef;

  /**
   * Overwrite the label prefix.
   */
  textkeyPrefix: string = "heizcheck";

  /**
   * steps where the help should not be visible
   */
  hideHelpForSteps = [
    'nutzungstyp',
  ];

  /**
   * for last step in case of short version we need a URL instead a step
   */
  nextUrl: string = null;

  /**
   * @inheritDoc
   * @param stepService
   * @param router
   * @param requestService
   * @param elementService
   * @param portalService
   * @param activatedroute
   */
  constructor(protected stepService: StepService, protected router: Router, protected requestService: HeizcheckRequestService, protected elementService: ElementService,
              protected portalService: PortalService, private activatedroute: ActivatedRoute, protected energyMapService: EnergyMapService) {
    super(stepService, router, elementService);
  }

  /**
   * @inheritDoc
   */
  ngOnInit() {
    // add the correct form config that we need in this steps and init the parent form
    this.formConfig = new VerbrauchsanalyseHeizcheckForm(this.energyMapService);
    super.ngOnInit();

    // deactivate autoscroll on first step
    if (this.steps.currentStep.name === 'nutzungstyp') {
      this.autoscrollOnLoad = false;
    }

    // get the current input data and preset it
    this.anfrage = this.requestService.getAnfrage();
    this.formConfig.setData(this.anfrage.verbrauchsanalyseInput);

    // check if we should show the short version or jump directly into the advisor.
    this.activatedroute.queryParams.subscribe(params => {
      if(params.hasOwnProperty(EinstiegComponent.PARAMETER_STEP)) {
        if(params[EinstiegComponent.PARAMETER_STEP] === EinstiegComponent.STEP_START) {
          // redirect to next path
          this.navigateToBillPath();
        }
        else {
          // save the version
          globalStep = params[EinstiegComponent.PARAMETER_STEP];
        }
      }
    });

    // set the nextUrl if we are on the last step and if we should show the short version
    if (this.steps.currentStep.name === 'heizungstyp' && globalStep === EinstiegComponent.STEP_SHORT) {
      this.nextUrl = this.portalService.getConfig('heizcheck.short.buttonlink');
      if(this.nextUrl) {
        this.nextUrl += this.nextUrl.indexOf("?") > -1 ? "&" : "?";
        this.nextUrl += EinstiegComponent.PARAMETER_STEP + "=" + EinstiegComponent.STEP_START;

        // if we have the short version running, we must catch changes on the form directly because there is no submit.
        this.subscriptions.push(this.form.valueChanges.subscribe(data => {
          if (this.form.valid) {
            this.saveData(data);
          }
        }));
      }
    }

    // some steps have no help text
    if (this.steps.currentStep && this.hideHelpForSteps.includes(this.steps.currentStep.name)) {
      this.hideHelp();
    }

    // update the previous step
    this.onStepUpdate();
  }

  /**
   * @inheritDoc
   * no autosubmit on the first step
   */
  setAutoSubmitAndInlineHelp() {
    super.setAutoSubmitAndInlineHelp();

    // avoid autosubmit on first step
    if(this.steps.currentStep.name === 'nutzungstyp' || (this.steps.currentStep.name === 'heizungstyp' && this.nextUrl)) {
      this.autoSubmit = false;
    }
  }

  /**
   * @inheritDoc
   * @param data
   */
  saveData(data:{[key: string]: any}) {
    // merge the data with the existing data
    this.anfrage.verbrauchsanalyseInput = {...this.anfrage.verbrauchsanalyseInput, ...data};
    this.requestService.setAnfrage(this.anfrage);

    // save the data also to the server
    this.requestService.saveDataToServer();

    super.saveData(data);
  }

  /**
   * Return the next step from a step list
   * @param stepList
   */
  getNextStepFromList(stepList: Step[]): any {
    // set all data again in the form to avoid using old data for toggle fields
    if(this.anfrage?.verbrauchsanalyseInput && Object.keys(this.anfrage.verbrauchsanalyseInput).length > 0 && this.formConfig) {
      this.formConfig.updateFields(this.anfrage.verbrauchsanalyseInput);
      // save the data again. In some case some fields are autoupdatetd
      this.saveData({});
    }

    return super.getNextStepFromList(stepList);
  }

  /**
   * @inheritDoc
   */
  onSubmit(): void {
    if(this.form.valid) {
      /* some inputs end the advisor */
      if (this.steps.currentStep.name === 'heizungstyp') {
        // save data
        this.saveData(this.formConfig.getData());

        // oben the next path
        this.navigateToBillPath();
        return;
      }
    }

    super.onSubmit();

    // if form was invalid, scroll to the error message
    if(this.form.invalid) {
      window.setTimeout(() => {
        if(this.formWrapper?.nativeElement) {
          this.elementService.scrollToElement(this.formWrapper.nativeElement);
        }
      }, 0)
    }
  }

  /**
   * Opens the next path after the entry section.
   * Also check if we hould stop here.
   */
  navigateToBillPath(): void {
    // we must stop for dentralized heating and building owner
    if (this.anfrage.verbrauchsanalyseInput.heizungstyp !== 'zentral' && this.anfrage.verbrauchsanalyseInput.gebaeudetyp === 'mehrfamilienhaus' && (this.anfrage.verbrauchsanalyseInput.nutzungstyp === 'hausverwalter' ||
      this.anfrage.verbrauchsanalyseInput.nutzungstyp === 'hauseigentuemer')) {
      const lastStep = this.router.url.replace(/\?.+/, "");
      this.router.navigateByUrl(this.portalService.currentPortalId + "/heizcheck/verbrauchsanalyse/ausstieg", {state: {lastStep: lastStep}});
      return;
    }

    // navigate to next section, depending on usertype and heatingsystem
    if ((this.anfrage.verbrauchsanalyseInput.nutzungstyp === 'mieter' || this.anfrage.verbrauchsanalyseInput.nutzungstyp === 'wohnungseigentuemer') &&
      this.anfrage.verbrauchsanalyseInput.heizungstyp === 'zentral' && this.anfrage.verbrauchsanalyseInput.gebaeudetyp === 'mehrfamilienhaus') {
      // path 1: heating bill
      this.router.navigateByUrl(this.portalService.currentPortalId + "/heizcheck/heizkostenabrechnung/start", {state: {lastStep: this.router.url}});
    }
    else {
      // path 2: energy bill
      this.router.navigateByUrl(this.portalService.currentPortalId + "/heizcheck/verbrauchsanalyse/basisinformationen/1", {state: {lastStep: this.router.url}});
    }
  }

  /**
   * @inheritDoc
   */
  onStepUpdate() {
    super.onStepUpdate();

    if(this.steps.currentStep.name === 'heizungstyp' && (this.anfrage?.verbrauchsanalyseInput?.nutzungstyp === 'mieter' || this.anfrage?.verbrauchsanalyseInput?.nutzungstyp === 'wohnungseigentuemer')) {
      this.helpTextkey += '.wohnung';
    }
  }

}
